import { useEffect } from 'react';
import type { Control, FieldValues, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'styled-components';

import { ColorName, Option } from 'types';

import { SelectWithControl } from 'components/SelectWithControl';

import { CollapsibleField } from '../CollapsibleField';
import { AddressDetailsPanel, CustomerDetailsPanel } from '../DetailPanels';
import {
  CODE_SELECT_MENU_MAX_WIDTH,
  codeSelectComponents,
  formatCodeOptionLabel,
} from '../codeSelect';
import {
  ORDER_ADDRESS_FIELDS as F,
  InitialAddresses,
  formatNameOptionLabel,
  useOrderAddressPicker,
} from './OrderAddressPicker.hooks';
import * as S from './OrderAddressPicker.styles';

type OrderAddressPickerProps = Readonly<{
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  /** Zone the customers are searched in. */
  zone: string;
  /** Customer shown from the start (e.g. the order being copied). */
  initialCustomer?: { code: string; name?: string } | null;
  /** Shipping / Goods shown from the start (the order being copied), if in the customer's lists. */
  initialAddresses?: InitialAddresses | null;
  /** Told while those initial addresses are still loading (the form must wait). */
  onPrefillingChange?: (isPrefilling: boolean) => void;
  /** Catalogue colour of the texts (the settings `barColor`); default colours without it. */
  color?: ColorName;
}>;

/** In the modals the book icon is always shown: it opens the list (by code or by name). */
const bookIcon = codeSelectComponents(true);

/**
 * Customer, Shipping Address and Goods Destination for the New Order / Copy Order modals:
 * a code select and a name select per block (both pick the same record) and the ↘ / ↗ toggle
 * with Address, City - State - Country (+ Payment Condition for the customer).
 * Writes the `ORDER_ADDRESS_FIELDS` of the caller's form.
 */
export function OrderAddressPicker({
  control,
  setValue,
  zone,
  initialCustomer,
  initialAddresses,
  onPrefillingChange,
  color,
}: OrderAddressPickerProps) {
  const { t } = useTranslation();
  // Catalogue colour also on the focused select and the hovered / selected option.
  const theme = useTheme();
  const accentColor = color ? theme.colors[color] : undefined;
  const picker = useOrderAddressPicker({ zone, setValue, initialCustomer, initialAddresses });
  const { isPrefilling } = picker;

  useEffect(() => {
    onPrefillingChange?.(isPrefilling);
  }, [isPrefilling, onPrefillingChange]);

  const codeSelect = (
    name: string,
    label: string,
    options: Option[],
    onSelect: (option: Option) => void,
    extra?: {
      onInputChange?: (value: string) => void;
      required?: boolean;
      isLoading?: boolean;
    }
  ) => (
    <SelectWithControl
      control={control}
      name={name}
      label={label}
      options={options}
      onSelect={onSelect}
      onInputChange={extra?.onInputChange}
      required={extra?.required}
      isLoading={extra?.isLoading}
      motive="filter"
      placeholder={t('common.typeAndSelect')}
      hideEmptyOption
      accentColor={accentColor}
      formatOptionLabel={formatCodeOptionLabel}
      components={bookIcon}
      menuMaxWidth={CODE_SELECT_MENU_MAX_WIDTH}
    />
  );

  const nameSelect = (
    name: string,
    options: Option[],
    onSelect: (option: Option) => void,
    onInputChange?: (value: string) => void,
    isLoading?: boolean
  ) => (
    <S.NameSlot>
      <SelectWithControl
        control={control}
        name={name}
        options={options}
        onSelect={onSelect}
        onInputChange={onInputChange}
        isLoading={isLoading}
        motive="filter"
        placeholder={t('common.typeAndSelect')}
        hideEmptyOption
        accentColor={accentColor}
        formatOptionLabel={formatNameOptionLabel}
        components={bookIcon}
        menuMaxWidth={CODE_SELECT_MENU_MAX_WIDTH}
      />
    </S.NameSlot>
  );

  return (
    <S.Picker $color={color}>
      <CollapsibleField
        data-testid="picker-customer"
        field={codeSelect(
          F.customer,
          t('customer.label'),
          picker.customerCodeOptions,
          picker.selectCustomer,
          {
            onInputChange: picker.searchCustomers,
            required: true,
            isLoading: picker.isSearching,
          }
        )}
        name={nameSelect(
          F.customerName,
          picker.customerNameOptions,
          picker.selectCustomer,
          picker.searchCustomers,
          picker.isSearching
        )}
        details={<CustomerDetailsPanel details={picker.details} withBuyer={false} />}
      />

      <CollapsibleField
        data-testid="picker-shipping"
        field={codeSelect(
          F.shipping,
          t('address.shipping'),
          picker.shippingCodeOptions,
          picker.selectShipping
        )}
        name={nameSelect(F.shippingName, picker.shippingNameOptions, picker.selectShipping)}
        details={
          <AddressDetailsPanel
            address={picker.shippingAddress}
            emptyMessage={t('address.selectShippingHint')}
          />
        }
      />

      <CollapsibleField
        data-testid="picker-goods"
        field={codeSelect(
          F.goods,
          t('address.goodsDestination'),
          picker.goodsCodeOptions,
          picker.selectGoods
        )}
        name={nameSelect(F.goodsName, picker.goodsNameOptions, picker.selectGoods)}
        details={
          <AddressDetailsPanel
            address={picker.goodsAddress}
            emptyMessage={t('address.selectGoodsHint')}
          />
        }
      />
    </S.Picker>
  );
}
